import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import {
  fetchValidationRules,
  fetchValidationRule,
  createValidationRule as createValidationRuleGobodo,
  updateValidationRule as updateValidationRuleGobodo,
  deleteValidationRule as deleteValidationRuleGobodo,
} from '../gobodo/gobodoValidationRuleService';
import { mockObjects } from './objectQueries';

// ============================================
// TYPES
// ============================================

export type ValidationOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterOrEqual'
  | 'lessOrEqual'
  | 'isBlank'
  | 'isNotBlank';

export type LogicOperator = 'AND' | 'OR';

export interface ValidationCondition {
  id: string;
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
  operator: ValidationOperator;
  value: string | number | boolean;
}

export interface ValidationRule {
  id: string;
  name: string;
  objectName: string;
  description?: string;
  isActive: boolean;

  // Condition logic
  conditions: ValidationCondition[];
  logicOperator: LogicOperator;

  // Error configuration
  errorMessage: string;
  errorLocation: 'top' | 'field';
  errorFieldId?: string; // If errorLocation is 'field'

  // Metadata
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

// ============================================
// MOCK DATA
// ============================================

export const mockValidationRules: ValidationRule[] = [];

// Generate simple validation rules for custom objects
// Note: In production, these would reference actual field IDs from the object's fields
mockObjects.filter(obj => obj.isActive).forEach(obj => {
  // Rule 1: Required field validation (generic example)
  mockValidationRules.push({
    id: `rule_${obj.apiName}_001`,
    name: `${obj.label} Name Required`,
    objectName: obj.apiName,
    description: `Ensures ${obj.label} name is always provided`,
    isActive: true,
    conditions: [
      {
        id: 'cond_001',
        fieldId: 'field_name',
        fieldLabel: 'Name',
        fieldType: 'text',
        operator: 'isBlank',
        value: ''
      }
    ],
    logicOperator: 'AND',
    errorMessage: `${obj.label} name is required`,
    errorLocation: 'field',
    errorFieldId: 'field_name',
    createdBy: 'System',
    createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'System'
  });

  // Rule 2: Email format validation (for objects that might have email)
  // Disabled - email format is validated in form validation, not via validation rules
  // The previous rule was inverted (triggered when email was NOT blank)
  // Email is optional, so we only validate format in the form, not via validation rules
  if (false && (obj.apiName === 'Client__c' || obj.apiName === 'Contact__c')) {
    mockValidationRules.push({
      id: `rule_${obj.apiName}_002`,
      name: 'Valid Email Format',
      objectName: obj.apiName,
      description: 'Ensures email address is properly formatted when provided',
      isActive: false,
      conditions: [
        {
          id: 'cond_002',
          fieldId: 'field_email',
          fieldLabel: 'Email',
          fieldType: 'email',
          operator: 'isNotBlank',
          value: ''
        }
      ],
      logicOperator: 'AND',
      errorMessage: 'Please enter a valid email address',
      errorLocation: 'field',
      errorFieldId: 'field_email',
      createdBy: 'Admin',
      createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      lastModifiedBy: 'Admin'
    });
  }

  // Rule 3: For Client object - High revenue validation
  if (obj.apiName === 'Client__c') {
    mockValidationRules.push({
      id: `rule_${obj.apiName}_003`,
      name: 'High Revenue Must Be Active',
      objectName: obj.apiName,
      description: 'Prevents high-value clients from being marked inactive',
      isActive: true,
      conditions: [
        {
          id: 'cond_003_1',
          fieldId: 'field_annual_revenue',
          fieldLabel: 'Annual Revenue',
          fieldType: 'currency',
          operator: 'greaterThan',
          value: 1000000
        },
        {
          id: 'cond_003_2',
          fieldId: 'field_status',
          fieldLabel: 'Status',
          fieldType: 'picklist',
          operator: 'equals',
          value: 'Inactive'
        }
      ],
      logicOperator: 'AND',
      errorMessage: 'High revenue clients (>$1M) cannot be marked as inactive',
      errorLocation: 'top',
      createdBy: 'Sales Manager',
      createdDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
      lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      lastModifiedBy: 'Sales Manager'
    });
  }
});

// ============================================
// QUERY KEYS
// ============================================

export const validationRuleKeys = {
  all: ['validation-rules'] as const,
  lists: () => [...validationRuleKeys.all, 'list'] as const,
  list: (objectName: string) => [...validationRuleKeys.lists(), objectName] as const,
  details: () => [...validationRuleKeys.all, 'detail'] as const,
  detail: (id: string) => [...validationRuleKeys.details(), id] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all validation rules for an object
 */
export function useValidationRules(objectName: string) {
  return useQuery({
    queryKey: validationRuleKeys.list(objectName),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchValidationRules(objectName);
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      return mockValidationRules.filter(rule => rule.objectName === objectName);
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get single validation rule
 */
export function useValidationRule(ruleId: string) {
  return useQuery({
    queryKey: validationRuleKeys.detail(ruleId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchValidationRule(ruleId);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const rule = mockValidationRules.find(r => r.id === ruleId);
      if (!rule) throw new Error('Validation rule not found');
      return rule;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!ruleId,
  });
}

/**
 * Create new validation rule
 */
export function useCreateValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleData: Omit<ValidationRule, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>) => {
      if (USE_GOBODO_API) {
        return createValidationRuleGobodo(ruleData);
      }
      await new Promise(resolve => setTimeout(resolve, 600));
      const newRule: ValidationRule = {
        ...ruleData,
        id: `rule_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      mockValidationRules.push(newRule);
      return newRule;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: validationRuleKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update validation rule
 */
export function useUpdateValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, updates }: { ruleId: string; updates: Partial<ValidationRule> }) => {
      if (USE_GOBODO_API) {
        return updateValidationRuleGobodo(ruleId, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const index = mockValidationRules.findIndex(r => r.id === ruleId);
      if (index === -1) throw new Error('Validation rule not found');
      mockValidationRules[index] = {
        ...mockValidationRules[index],
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      return mockValidationRules[index];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: validationRuleKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: validationRuleKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete validation rule
 */
export function useDeleteValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, objectName }: { ruleId: string; objectName: string }) => {
      if (USE_GOBODO_API) {
        await deleteValidationRuleGobodo(ruleId);
        return { ruleId, objectName };
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const index = mockValidationRules.findIndex(r => r.id === ruleId);
      if (index === -1) throw new Error('Validation rule not found');
      mockValidationRules.splice(index, 1);
      return { ruleId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: validationRuleKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone validation rule
 */
export function useCloneValidationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      if (USE_GOBODO_API) {
        const rule = await fetchValidationRule(ruleId);
        const { id: _id, createdBy: _cb, createdDate: _cd, lastModified: _lm, lastModifiedBy: _lmb, ...rest } = rule;
        return createValidationRuleGobodo({ ...rest, name: `${rule.name} (Copy)`, isActive: false });
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const rule = mockValidationRules.find(r => r.id === ruleId);
      if (!rule) throw new Error('Validation rule not found');
      const clonedRule: ValidationRule = {
        ...rule,
        id: `rule_${Date.now()}`,
        name: `${rule.name} (Copy)`,
        isActive: false,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
      };
      mockValidationRules.push(clonedRule);
      return clonedRule;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: validationRuleKeys.list(data.objectName) });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get operator label
 */
export function getValidationOperatorLabel(operator: ValidationOperator): string {
  const labels: Record<ValidationOperator, string> = {
    equals: 'equals',
    notEquals: 'does not equal',
    contains: 'contains',
    notContains: 'does not contain',
    startsWith: 'starts with',
    greaterThan: 'greater than',
    lessThan: 'less than',
    greaterOrEqual: 'greater or equal to',
    lessOrEqual: 'less or equal to',
    isBlank: 'is blank',
    isNotBlank: 'is not blank'
  };
  return labels[operator];
}

/**
 * Get available operators for field type
 */
export function getValidationOperatorsForFieldType(fieldType: string): ValidationOperator[] {
  const operatorMap: Record<string, ValidationOperator[]> = {
    text: ['equals', 'notEquals', 'contains', 'notContains', 'startsWith', 'isBlank', 'isNotBlank'],
    email: ['equals', 'notEquals', 'contains', 'notContains', 'isBlank', 'isNotBlank'],
    phone: ['equals', 'notEquals', 'contains', 'notContains', 'isBlank', 'isNotBlank'],
    number: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isBlank', 'isNotBlank'],
    currency: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isBlank', 'isNotBlank'],
    date: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isBlank', 'isNotBlank'],
    datetime: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'isBlank', 'isNotBlank'],
    checkbox: ['equals', 'notEquals'],
    picklist: ['equals', 'notEquals', 'isBlank', 'isNotBlank'],
    lookup: ['equals', 'notEquals', 'isBlank', 'isNotBlank']
  };

  return operatorMap[fieldType] || ['equals', 'notEquals', 'isBlank', 'isNotBlank'];
}

/**
 * Format condition value for display
 */
export function formatConditionValue(value: string | number | boolean, fieldType: string): string {
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (fieldType === 'currency') return `$${Number(value).toLocaleString()}`;
  return String(value);
}

/**
 * Test validation rule against sample data
 */
export function testValidationRule(rule: ValidationRule, testData: Record<string, any>): {
  passes: boolean;
  errorMessage?: string;
} {
  // Evaluate all conditions
  const conditionResults = rule.conditions.map(condition => {
    const fieldValue = testData[condition.fieldId];

    switch (condition.operator) {
      case 'equals':
        return fieldValue == condition.value;
      case 'notEquals':
        return fieldValue != condition.value;
      case 'contains':
        return String(fieldValue || '').includes(String(condition.value));
      case 'notContains':
        return !String(fieldValue || '').includes(String(condition.value));
      case 'startsWith':
        return String(fieldValue || '').startsWith(String(condition.value));
      case 'greaterThan':
        return Number(fieldValue) > Number(condition.value);
      case 'lessThan':
        return Number(fieldValue) < Number(condition.value);
      case 'greaterOrEqual':
        return Number(fieldValue) >= Number(condition.value);
      case 'lessOrEqual':
        return Number(fieldValue) <= Number(condition.value);
      case 'isBlank':
        return !fieldValue || fieldValue === '';
      case 'isNotBlank':
        return fieldValue && fieldValue !== '';
      default:
        return false;
    }
  });

  // Apply logic operator
  const ruleTriggered = rule.logicOperator === 'AND'
    ? conditionResults.every(r => r)
    : conditionResults.some(r => r);

  return {
    passes: !ruleTriggered, // Rule passes if NOT triggered (validation error not raised)
    errorMessage: ruleTriggered ? rule.errorMessage : undefined
  };
}

// ============================================
// VALIDATION ENFORCEMENT
// ============================================

export interface ValidationError {
  ruleId: string;
  ruleName: string;
  errorMessage: string;
  errorLocation: 'top' | 'field';
  fieldId?: string;
  fieldLabel?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validate record data against all active validation rules for an object
 * Call this before saving any record to enforce validation rules
 *
 * @param objectName - The API name of the object (e.g., 'Client__c')
 * @param recordData - The record data to validate (field IDs mapped to values)
 * @returns ValidationResult with isValid flag and array of errors
 *
 * @example
 * const result = validateRecord('Client__c', { field_name: 'John', field_email: '' });
 * if (!result.isValid) {
 *   // Show errors to user
 *   result.errors.forEach(err => console.log(err.errorMessage));
 * }
 */
export function validateRecord(
  objectName: string,
  recordData: Record<string, any>
): ValidationResult {
  // Get all active rules for this object
  const activeRules = mockValidationRules.filter(
    rule => rule.objectName === objectName && rule.isActive
  );

  if (activeRules.length === 0) {
    return { isValid: true, errors: [] };
  }

  const errors: ValidationError[] = [];

  // Test each rule
  for (const rule of activeRules) {
    const result = testValidationRule(rule, recordData);

    if (!result.passes) {
      errors.push({
        ruleId: rule.id,
        ruleName: rule.name,
        errorMessage: rule.errorMessage,
        errorLocation: rule.errorLocation,
        fieldId: rule.errorFieldId,
        fieldLabel: rule.conditions[0]?.fieldLabel
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * React hook for validation - can be used in components
 * Provides memoized validation function
 */
export function useRecordValidation(objectName: string) {
  const validate = (recordData: Record<string, any>): ValidationResult => {
    return validateRecord(objectName, recordData);
  };

  return { validate };
}
